<script setup lang="ts">
/**
 * One icon of the shared office ribbon set: the same drawing and colours for a
 * command in Calc and in Write. `md` is the inline button size, `lg` the big
 * button above a label. A font- or fill-colour button passes the colour it
 * would apply as `current`; the icon's colour bar then shows it.
 *
 * Formatting letters (NX_OFFICE_LETTERS) render as plain text rather than SVG,
 * so the font keeps its hinting and they stay as sharp as any label.
 */
import { computed } from 'vue';
import {
  NX_OFFICE_ICONS,
  NX_OFFICE_LETTERS,
  type NxOfficeIconName,
  type NxOfficeIconNode,
  type NxOfficeLetter,
} from './officeIcons';
import '../office-icon.css';

const props = withDefaults(
  defineProps<{
    name: NxOfficeIconName;
    size?: 'md' | 'lg';
    current?: string | null;
  }>(),
  { size: 'md', current: null }
);

const letter = computed<NxOfficeLetter | null>(() =>
  props.name in NX_OFFICE_LETTERS
    ? NX_OFFICE_LETTERS[props.name as keyof typeof NX_OFFICE_LETTERS]
    : null
);
const nodes = computed<readonly NxOfficeIconNode[]>(() =>
  props.name in NX_OFFICE_ICONS ? NX_OFFICE_ICONS[props.name as keyof typeof NX_OFFICE_ICONS] : []
);
const style = computed(() =>
  props.current ? { '--office-icon-current': props.current } : undefined
);
const letterClasses = computed(() => {
  const l = letter.value;
  if (!l) return [];
  return [
    'nx-office-letter',
    l.weight === 'bold' ? 'nx-office-letter--bold' : null,
    l.size === 'small' ? 'nx-office-letter--small' : null,
    l.bar ? `nx-office-letter--${l.bar}` : null,
    l.caret ? `nx-office-letter--caret-${l.caret}` : null,
  ];
});

function roleClasses(node: NxOfficeIconNode): (string | null)[] {
  return [`s-${node[2] ?? 'ink'}`, node[3] ? `f-${node[3]}` : null];
}
</script>

<template>
  <span
    v-if="letter"
    :class="['nx-office-icon', `nx-office-icon--${size}`, ...letterClasses]"
    :data-icon="name"
    :style="style"
    aria-hidden="true"
  >
    <span class="nx-office-letter__glyph">{{ letter.text }}</span>
    <span v-if="letter.bar" class="nx-office-letter__bar" />
    <svg v-if="letter.caret" class="nx-office-letter__caret" viewBox="0 0 8 5">
      <path :d="letter.caret === 'up' ? 'M1 4.25 4 1.25 7 4.25' : 'M1 0.75 4 3.75 7 0.75'" />
    </svg>
  </span>
  <svg
    v-else
    :class="['nx-office-icon', `nx-office-icon--${size}`]"
    :data-icon="name"
    :style="style"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <component
      :is="node[0]"
      v-for="(node, i) in nodes"
      :key="i"
      v-bind="node[1]"
      :class="roleClasses(node)"
    />
  </svg>
</template>
